import type { AudioDoc } from './logic'
import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'
import * as TypedAssert from 'typed-assert'

// Create an array of events that we use as player state indication.
// We want to be able to iterate over them, which is why we create
// an array first.
// Then we create a union type from that which gives us approx.:
// ```
// PlaybackType = 'play' | 'pause' | ...
// ```
// For reference see:
// See https://dev.to/shakyshane/2-ways-to-create-a-union-from-an-array-in-typescript-1kd6
// See https://www.typescriptlang.org/docs/handbook/2/indexed-access-types.html
const _playbackStates = ['play', 'pause', 'ended'] as const
export type PlaybackState = (typeof _playbackStates)[number]

const _interruptionEvents = ['loadstart', 'stalled', 'waiting'] as const
export type InterruptionEvent = (typeof _interruptionEvents)[number]

const _interruptionResetEvents = [
  'loadeddata',
  'playing',
  'pause',
  'timeupdate',
] as const
export type InterruptionResetEvent = (typeof _interruptionResetEvents)[number]
/**
 * Audio play, pause and forward/back rewind UI control.
 */
export class PlaybackControl {
  private _audio: AudioDoc

  // True if audio is initially loaded enough to start the playback.
  private _canplay: boolean = false

  // The desired state of playback. There are situations where we cannot
  // immediately set the desired state, like playing when audio is not
  // initially loaded or pausing when playback has not yet begun. Thus we
  // cache it here.
  private _desiredState: 'play' | 'pause' = 'pause'

  // Track the current play request so a pause can cancel it without waiting
  // for HTMLMediaElement.play() to settle. The request number prevents an old
  // play promise from changing state after a newer request has taken over.
  private _playRequest: number = 0
  private _playPending: boolean = false
  private _stateChangeScheduled: boolean = false

  // This flag is set if playback stopped because there was no data.
  // It is reset when playing resumes or when the stream is paused.
  private _interruptedByNetwork: boolean = false

  // Audio position tracker, saves position to the backend.
  private _positionTracker: PositionTracker

  // Callback to track audio state changes (playing, paused, etc).
  private _stateUpdateCallback: (state: PlaybackState) => void

  constructor(
    audio: AudioDoc,
    stateUpdateCallback: (state: PlaybackState) => void,
  ) {
    this._audio = audio
    this._stateUpdateCallback = stateUpdateCallback
    this._positionTracker = new PositionTracker(audio)
  }

  /**
   * Init event listeners.
   *
   * We can not do this in the constructor as Vue 3 reactivity system
   * is not initialized and `this` is not reactive yet.
   * See #4824.
   */
  public initEventListeners(): void {
    // Usually (everywhere except mobile iOS) we get the "canplay" event
    // right out, as browser starts downloading the audio when the page
    // is loaded (we have <audio> element in the HTMl).
    //
    // Also the "canplay" event is sent repeatedyly so we may not worry about
    // loosing it (but more reliable way might be to also check the
    // audio.readyState, see
    // https://developer.mozilla.org/en-US/docs/Web/API/HTMLAudioElement/Audio#determining_when_playback_can_begin
    //
    // On mobile Safari, the download does not start and, what is worse, setting
    // audio.currentTime does nothing (it just remains zero) so we can not set
    // the future desired start position.

    // Only relevant for the native audio player, the 'intentchange'
    // event is sent by the native audio adapter.
    // See frontend/src/services/player/native-audio.ts
    this._audio.addListener('intentchange', () => {
      // Synchronize requested playback state with external actions. For
      // example, a lock-screen command may supersede a queued UI command.
      this._desiredState = this._audio.paused ? 'pause' : 'play'
    })
    this._audio.addListener('canplay', () => this._handleCanplayEvent())
    this._audio.addListener('playing', () => this._handlePlayingEvent())

    // These events signal interruption of playback due to network.
    _interruptionEvents.forEach((eventName) =>
      this._audio.addListener(eventName, () => {
        this._interruptedByNetwork = true
      }),
    )

    // These events signal that the network interruption is resolved.
    _interruptionResetEvents.forEach((eventName) =>
      this._audio.addListener(eventName, () => {
        this._interruptedByNetwork = false
      }),
    )

    // We forward these events to the `_stateUpdateCallback`.
    _playbackStates.forEach((eventName) =>
      this._audio.addListener(eventName, (event: Event) =>
        this._invokeStateUpdateCallback(event),
      ),
    )
  }

  _handleCanplayEvent(): void {
    if (this._canplay) {
      // The "canplay" event is sent repeatedly, exit.
      return
    }

    this._canplay = true

    // The user could have clicked play before audio was loaded.
    this._applyDesiredState()
  }

  _handlePlayingEvent(): void {
    // While the audio was preparing to play the user could have paused it.
    this._applyDesiredState()
  }

  _invokeStateUpdateCallback(event: Event): void {
    TypedAssert.isOneOf(event.type, _playbackStates)
    if (event.type === 'ended') {
      // Audio has ended, reset the desired state to "pause",
      // so the player shows the "Play" button again.
      this._desiredState = 'pause'
    }
    this._stateUpdateCallback(event.type)

    if (event.type !== 'play') {
      // Save the final position immediately instead of waiting for the next
      // periodic check, which will skip saving once playback is paused.
      void this._positionTracker.saveAudioPosition()
    }
  }

  /**
   * Resume playback.
   *
   * If the audio is not yet loaded, we call `audio.load()`
   * and the playback starts later, in the `canplay` event handler.
   */
  private _apply_play(): void {
    if (!this._audio.paused || this._playPending) {
      return
    }

    const playRequest = ++this._playRequest
    this._playPending = true
    void this._audio.play().then(
      () => this._handlePlayResolved(playRequest),
      (error: unknown) => this._handlePlayRejected(playRequest, error),
    )
  }

  private _handlePlayResolved(playRequest: number): void {
    if (playRequest === this._playRequest) {
      this._playPending = false
    }
  }

  private _handlePlayRejected(playRequest: number, error: unknown): void {
    const superseded = playRequest !== this._playRequest

    // Calling pause() rejects a pending play promise with AbortError. This is
    // expected when a pause has canceled an earlier playback request.
    const expectedAbort =
      superseded && error instanceof DOMException && error.name === 'AbortError'
    if (!expectedAbort) {
      Sentry.captureException(error)
    }

    if (!superseded) {
      this._playPending = false
      // Set the controls to paused so the next toggle retries playback instead
      // of issuing a redundant pause.
      this._desiredState = 'pause'
      this._apply_pause()
    }
  }

  private _apply_pause(): void {
    const playPending = this._playPending
    if (playPending) {
      // Ignore any later settlement from the cancelled play request and allow
      // a subsequent play request to start immediately.
      ++this._playRequest
      this._playPending = false
    }

    if (playPending || !this._audio.paused) {
      void this._audio.pause().then(
        () => {
          // Native pause is asynchronous. A newer play request may have
          // arrived while it was in flight, so apply the latest intent now.
          if (this._desiredState === 'play') {
            this._applyDesiredState()
          }
        },
        (error: unknown) => Sentry.captureException(error),
      )
    }
  }

  private _applyDesiredState(): void {
    // When play and pause are called back-to-back, apply only the last request.
    // The update finishes as soon as play() is called, so a later pause is not
    // blocked while playback starts.
    if (this._stateChangeScheduled) {
      return
    }
    this._stateChangeScheduled = true
    void Promise.resolve().then(() => {
      try {
        if (this._desiredState === 'play') {
          this._apply_play()
        } else {
          this._apply_pause()
        }
      } catch (error) {
        Sentry.captureException(error)
      } finally {
        this._stateChangeScheduled = false
      }
    })
  }

  play(): void {
    this._desiredState = 'play'
    this._applyDesiredState()
  }

  pause(): void {
    this._desiredState = 'pause'
    this._applyDesiredState()
  }

  togglePlayback(): void {
    this._desiredState === 'play' ? this.pause() : this.play()
  }

  forward(seconds: number = 15): void {
    this._audio.currentTime += seconds
  }

  backward(seconds: number = 15): void {
    this._audio.currentTime -= seconds
  }

  get audioLoaded(): boolean {
    return this._canplay
  }

  get paused(): boolean {
    return this._desiredState === 'pause'
  }

  get interruptedByNetwork(): boolean {
    return this._interruptedByNetwork
  }

  /**
   * Stop saving the audio position.
   */
  async stopPositionTracking(): Promise<void> {
    await this._positionTracker.stop()
  }
}

/*
 * Audio position tracker.
 *
 * An instance of this class is created in PlaybackControl constructor, and
 * it starts a timer to track audio position.
 *
 * The logic is based on periodically checking the ``currentTime`` audio
 * property and comparing it to the last saved position. Pausing or finishing
 * playback triggers an immediate save between those periodic checks.
 */
class PositionTracker {
  private _audio: AudioDoc
  private _lastSavedPosition: number
  // Each save waits for the previous save to finish before starting. This
  // prevents an older, slower request from overwriting a newer position saved
  // when playback pauses or ends.
  private _lastSaveFinished: Promise<void> = Promise.resolve()
  private _stopped: boolean = false

  constructor(audio: AudioDoc) {
    this._audio = audio
    this._lastSavedPosition = this._audio.doc.user.audio_position

    this.startTracking()
  }

  /*
   * Stop the tracking loop.
   */
  async stop(): Promise<void> {
    this._stopped = true

    // Stopping a session can remove its audio listeners before the queued
    // pause happens, so explicitly save any position that is still playing.
    if (!this._audio.paused) {
      await this.saveAudioPosition()
    }
    await this._lastSaveFinished
  }

  /*
   * Wrapper around standard setTimer() to have a better control over the flow.
   */
  wait(): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, 10 * 1000))
  }

  /*
   * Check if audio position changed.
   *
   *
   * Compare the last seen position with current audio position, return true if
   * they are different.
   */
  isPositionChanged(): boolean {
    if (Math.floor(this._audio.currentTime) === this._lastSavedPosition) {
      return false
    }
    return true
  }

  /*
   * Start tracking audio position.
   *
   * Save changed positions while playback is running.
   */
  async startTracking(): Promise<void> {
    while (!this._stopped) {
      await this.wait()
      if (!this._stopped && !this._audio.paused && this.isPositionChanged()) {
        await this.saveAudioPosition()
      }
    }
  }

  /*
   * Send the backend request to save the current audio position.
   */
  async saveAudioPosition(): Promise<void> {
    const position = Math.floor(this._audio.currentTime)
    const doc = this._audio.doc

    const previousSave = this._lastSaveFinished
    const savePosition = async (): Promise<void> => {
      await previousSave
      try {
        if (position === this._lastSavedPosition) {
          doc.user.audio_position = position
          return
        }

        await backend.saveAudioPosition(doc, position)
        this._lastSavedPosition = position
        doc.user.audio_position = position
      } catch (error) {
        Sentry.captureException(error)
      }
    }

    this._lastSaveFinished = savePosition()
    await this._lastSaveFinished
  }
}
